import { NmLoader } from '../NmLoader';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileSheet } from '../MobileSheet';
import { NmButton } from '../NmButton';
import { NmIsoDateField } from '../NmIsoDateField';
import {
  PDC_COMPARE_PRESET_DAYS,
  type PdcComparePreset,
  detectComparePreset,
} from '../../utils/pdcCompareRange';
import {
  WORKOUT_DETAIL_NESTED_SHEET_Z,
  WORKOUT_DETAIL_SHEET_Z,
} from '../../features/workout/workoutOverlayZIndex';

interface Props {
  compareStart: string;
  compareEnd: string;
  /** Workout day — compare window always ends here. */
  rangeEndDate: string;
  loading?: boolean;
  onApply: (preset: PdcComparePreset, custom?: { start: string; end: string }) => void;
}

/** Compact compare-range selector with a custom range bottom sheet. */
export function PdcCompareRangeControls({
  compareStart,
  compareEnd,
  rangeEndDate,
  loading,
  onApply,
}: Props) {
  const { t } = useTranslation();
  const [customOpen, setCustomOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(compareStart);
  const [draftEnd, setDraftEnd] = useState(compareEnd);

  const activePreset = detectComparePreset(compareStart, compareEnd, rangeEndDate);

  useEffect(() => {
    if (!customOpen) return;
    setDraftStart(compareStart);
    setDraftEnd(compareEnd);
  }, [customOpen, compareStart, compareEnd]);

  const openCustom = () => {
    setDraftStart(compareStart);
    setDraftEnd(compareEnd);
    setCustomOpen(true);
  };

  const applyCustom = () => {
    if (!draftStart || !draftEnd || draftStart > draftEnd) return;
    onApply('custom', { start: draftStart, end: draftEnd });
    setCustomOpen(false);
  };

  return (
    <>
      <div className="pdc-chart__range-row">
        <select
          className="pdc-chart__range-select"
          aria-label={t('pdcChart.compare')}
          value={String(activePreset)}
          onChange={(event) => {
            const next = event.target.value;
            if (next === 'custom') openCustom();
            else if (next === 'all') onApply('all');
            else onApply(Number(next) as PdcComparePreset);
          }}
        >
          {PDC_COMPARE_PRESET_DAYS.map((days) => <option key={days} value={days}>{t(`pdcChart.range.${days}`)}</option>)}
          <option value="all">{t('pdcChart.range.all')}</option>
          <option value="custom">{t('pdcChart.range.custom')}</option>
        </select>
        {loading && <span className="pdc-chart__range-loading"><NmLoader size="sm" inline label={t('common.loading')} /></span>}
      </div>

      <MobileSheet
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        title={t('pdcChart.customRangeTitle')}
        zIndex={WORKOUT_DETAIL_SHEET_Z}
        footer={(
          <NmButton variant="primary" className="pdc-range-sheet__apply" onClick={applyCustom}>
            {t('pdcChart.applyRange')}
          </NmButton>
        )}
      >
        <div className="pdc-range-sheet">
          <label className="pdc-range-sheet__field">
            <span className="pdc-range-sheet__label">{t('pdcChart.from')}</span>
            <NmIsoDateField
              value={draftStart}
              onChange={setDraftStart}
              max={draftEnd || rangeEndDate}
              sheetZIndex={WORKOUT_DETAIL_NESTED_SHEET_Z}
            />
          </label>
          <label className="pdc-range-sheet__field">
            <span className="pdc-range-sheet__label">{t('pdcChart.to')}</span>
            <NmIsoDateField
              value={draftEnd}
              onChange={setDraftEnd}
              min={draftStart}
              max={rangeEndDate}
              sheetZIndex={WORKOUT_DETAIL_NESTED_SHEET_Z}
            />
          </label>
        </div>
      </MobileSheet>
    </>
  );
}
